<template>
  <section class="invoice-preview-wrapper">

    <!-- Alert: No item found -->
    <b-alert
      variant="danger"
      :show="invoiceData === undefined"
    >
      <h4 class="alert-heading">
        Error fetching invoice data
      </h4>
      <div class="alert-body">
        No invoice found with this invoice id. Check
        <b-link
          class="alert-link"
          :to="{ name: 'apps-pedido-list'}"
        >
          Invoice List
        </b-link>
        for other invoices.
      </div>
    </b-alert>

    <b-row
      v-if="invoiceData"
      id="report-preview"
      class="invoice-preview"
    >

      <!-- Col: Left (Invoice Container) -->
      <b-col
        cols="12"
        xl="9"
        md="8"
      >
        <b-card
          no-body
          class="invoice-preview-card"
        >
          <!-- Header -->
          <b-card-body class="invoice-padding pb-0">

            <div class="d-flex justify-content-between flex-md-row flex-column invoice-spacing mt-0">

              <!-- Header: Left Content -->
              <div>
                <div class="logo-wrapper">
                  <h3 class="text-primary invoice-logo">
                    Ativadores de Vendas
                  </h3>
                </div>
                <b-card-text class="mb-25" v-if="invoiceData.bling_chave_acesso">
                  <span class="font-weight-bold">Número da NF-e:</span> {{ invoiceData.bling_numero_nfe }}
                </b-card-text>
                <b-card-text class="mb-25" v-if="invoiceData.bling_chave_acesso">
                  <span class="font-weight-bold">Chave de Acesso:</span> {{ invoiceData.bling_chave_acesso }}
                </b-card-text>
<!--                <b-card-text class="mb-25">-->
<!--                  San Diego County, CA 91905, USA-->
<!--                </b-card-text>-->
<!--                <b-card-text class="mb-0">-->
<!--                  +1 (123) 456 7891, +44 (876) 543 2198-->
<!--                </b-card-text>-->
              </div>

              <!-- Header: Right Content -->
              <div class="mt-md-0 mt-2">
                <h4 class="invoice-title">
                  Pedido
                  <span class="invoice-number">#{{ invoiceData.id }}</span>
                </h4>
                <div class="invoice-date-wrapper">
                  <p class="invoice-date-title">
                    Emissão:
                  </p>
                  <p class="invoice-date">
                    {{ invoiceData.data_inclusao | formatDateBr }}
                  </p>
                </div>
                <div class="invoice-date-wrapper">
                  <p class="invoice-date-title">
                    Vencimento:
                  </p>
                  <p class="invoice-date">
                    {{ invoiceData.data_venda | formatDateBr }}
                  </p>
                </div>
              </div>
            </div>
          </b-card-body>

          <!-- Spacer -->
          <hr class="invoice-spacing">

          <!-- Invoice Client & Payment Details -->
          <b-card-body
            v-if="invoiceData.cliente"
            class="invoice-padding pt-0"
          >
            <b-row class="invoice-spacing">

              <!-- Col: Invoice To -->
              <b-col
                cols="12"
                xl="6"
                class="p-0"
              >
                <h6 class="mb-2">
                  Fatura para:
                </h6>
                <h6 class="mb-25">
                  {{ invoiceData.cliente.nome }}
                </h6>
                <p class="card-text mb-25">
                  {{ invoiceData.cliente.documento | formatDocumento }}
                </p>
                <p class="card-text mb-25">
                  {{ invoiceData.pedido_endereco.street }}, {{ invoiceData.pedido_endereco.locality }}, {{ invoiceData.pedido_endereco.city && invoiceData.pedido_endereco.city }} - {{ invoiceData.pedido_endereco.region_code && invoiceData.pedido_endereco.region_code }}
                </p>
                <p class="card-text mb-25">
                  {{ invoiceData.cliente.contatos[0].cliente_contato_celular | formatPhone }}
                </p>
                <p class="card-text mb-0">
                  {{ invoiceData.cliente.usuarios[0].login }}
                </p>
              </b-col>

              <!-- Col: Payment Details -->
              <b-col
                xl="6"
                cols="12"
                class="p-0 mt-xl-0 mt-2 d-flex justify-content-xl-end"
              >
                <div>
                  <h6 class="mb-2">
                    Detalhes do pagamento:
                  </h6>
                  <table>
                    <tbody>
                      <tr>
                        <td class="pr-1">
                          Total Pedido:
                        </td>
                        <td><span class="font-weight-bold">{{ valorTotalFiltrado | currency }}</span></td>
                      </tr>
                      <tr>
                        <td class="pr-1">
                          Forma de pagamento:
                        </td>
                        <td>{{ resolverMetodoPagamento(paymentDetails.id) }}</td>
                      </tr>
                      <tr v-if="paymentDetails.id === 4">
                        <td class="pr-1">
                          Parcelas:
                        </td>
                        <td>{{ invoiceData.parcelas }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </b-col>
            </b-row>
          </b-card-body>

          <!-- Invoice Description: Table -->
          <b-table-lite
            responsive
            :items="filteredPedidoItems"
            :fields="[
              { key: 'item', sortable: true, label: 'Produto' },
              { key: 'quantidade', sortable: true, label: 'Qtd.' },
              { key: 'valor_unitario', sortable: true, label: 'Valor Unit.' },
              { key: 'valor_total_item', sortable: true, label: 'Total' },
            ]"
          >
            <template #cell(item)="data">
              <b-card-text class="font-weight-bold mb-25">
                {{ data.value.item_nome }}
              </b-card-text>
            </template>

            <template #cell(valor_unitario)="data">
              <b-card-text class="font-weight-bold mb-25">
                {{ data.value | currency }}
              </b-card-text>
            </template>
            <template #cell(valor_total_item)="data">
              <b-card-text class="font-weight-bold mb-25">
                {{ data.value | currency }}
              </b-card-text>
            </template>

          </b-table-lite>

          <!-- Invoice Description: Total -->
          <b-card-body class="invoice-padding pb-0">
            <b-row>

              <!-- Col: Sales Persion -->
              <b-col
                cols="12"
                md="6"
                class="mt-md-0 mt-3"
                order="2"
                order-md="1"
              >
                <b-card-text class="mb-0">
                  <span class="font-weight-bold">Comprador:</span>
                  <span class="ml-75">{{ invoiceData.cliente.nome }}</span>
                </b-card-text>
              </b-col>

              <!-- Col: Total -->
              <b-col
                cols="24"
                md="6"
                class="mt-md-6 d-flex justify-content-end"
                order="1"
                order-md="2"
              >
                <div class="invoice-total-wrapper">
                  <div class="invoice-total-item">
                    <p class="invoice-total-title">
                      Total:
                    </p>
                    <p class="invoice-total-amount">
                      {{ valorTotalFiltrado | currency }}
                    </p>
                  </div>
                </div>
              </b-col>
            </b-row>
          </b-card-body>

          <!-- Spacer -->
          <span class="my-1" />

          <!-- Note -->

        </b-card>
      </b-col>

      <!-- Right Col: Card -->
      <b-col
        cols="12"
        md="4"
        xl="3"
        class="invoice-actions"
      >
        <b-card>

<!--          &lt;!&ndash; Button: Send Invoice &ndash;&gt;-->
<!--          <b-button-->
<!--            v-ripple.400="'rgba(255, 255, 255, 0.15)'"-->
<!--            v-b-toggle.sidebar-send-invoice-->
<!--            variant="primary"-->
<!--            class="mb-75"-->
<!--            block-->
<!--          >-->
<!--            Enviar fatura-->
<!--          </b-button>-->

          <!-- Button: Print -->
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            @click="printInvoice"
          >
            Imprimir
          </b-button>

          <!-- Button: Download DANFE -->
          <b-button
              v-if="invoiceData.bling_chave_acesso"
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-danger"
              class="mb-75"
              block
              @click="downloadLink(invoiceData, 'bling_link_danfe')"
          >
            Download DANFE
          </b-button>

          <!-- Button: Download PDF -->
          <b-button
              v-if="invoiceData.bling_chave_acesso"
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-danger"
              class="mb-75"
              block
              @click="downloadLink(invoiceData, 'bling_link_pdf')"
          >
            Download PDF
          </b-button>

          <!-- Shipping Info -->
          <div v-if="invoiceData.metodo_envio" class="mt-1 mb-1">
            <h6 class="mb-50">Envio</h6>
            <b-card-text class="mb-25">
              <strong>Método:</strong> {{ invoiceData.metodo_envio }}
            </b-card-text>
            <b-card-text v-if="invoiceData.prazo_entrega" class="mb-25">
              <strong>Prazo:</strong> {{ invoiceData.prazo_entrega }} dias úteis
            </b-card-text>
            <b-card-text v-if="invoiceData.codigo_rastreio" class="mb-25">
              <strong>Rastreio:</strong>
              <b-badge variant="info">{{ invoiceData.codigo_rastreio }}</b-badge>
            </b-card-text>
          </div>

          <!-- Button: Gerar Etiqueta Correios -->
          <b-button
            v-if="['Paid','PAID','CONFIRMED','RECEIVED'].includes(invoiceData.status)"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="warning"
            class="mb-75"
            block
            :disabled="gerandoEtiqueta"
            @click="gerarEtiquetaCorreios"
          >
            <b-spinner v-if="gerandoEtiqueta" small class="mr-50" />
            <feather-icon v-else icon="TruckIcon" class="mr-50" />
            {{ invoiceData.etiqueta_url ? 'Etiqueta Gerada' : 'Gerar Etiqueta de Envio' }}
          </b-button>

          <!-- Button: Imprimir Etiqueta -->
          <b-button
            v-if="invoiceData.etiqueta_url"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="info"
            class="mb-75"
            block
            :disabled="baixandoEtiqueta"
            @click="imprimirEtiqueta"
          >
            <b-spinner v-if="baixandoEtiqueta" small class="mr-50" />
            <feather-icon v-else icon="PrinterIcon" class="mr-50" />
            Imprimir Etiqueta de Envio
          </b-button>

          <!-- Button: Rastrear Encomenda -->
          <b-button
            v-if="invoiceData.codigo_rastreio"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="success"
            class="mb-75"
            block
            :disabled="rastreando"
            @click="rastrearEncomenda"
          >
            <b-spinner v-if="rastreando" small class="mr-50" />
            <feather-icon v-else icon="NavigationIcon" class="mr-50" />
            Rastrear Encomenda
          </b-button>
        </b-card>

        <!-- Tracking Timeline Card -->
        <b-card v-if="rastreioEventos.length > 0" class="mt-1">
          <h6 class="mb-1">Rastreamento</h6>
          <app-timeline>
            <app-timeline-item
              v-for="(evento, index) in rastreioEventos"
              :key="index"
              :variant="resolverCorEvento(evento.descricao)"
            >
              <div class="d-flex flex-sm-row flex-column flex-wrap justify-content-between mb-1 mb-sm-0">
                <h6 class="mb-0">{{ evento.descricao }}</h6>
                <small class="text-muted">{{ evento.dtHrCriado | formatDateTimeBr }}</small>
              </div>
              <p class="mb-0" v-if="evento.unidade">
                {{ evento.unidade.nome }} - {{ evento.unidade.endereco.cidade }}/{{ evento.unidade.endereco.uf }}
              </p>
              <p class="mb-0" v-if="evento.unidadeDestino">
                <small>Destino: {{ evento.unidadeDestino.nome }} - {{ evento.unidadeDestino.endereco.cidade }}/{{ evento.unidadeDestino.endereco.uf }}</small>
              </p>
            </app-timeline-item>
          </app-timeline>
        </b-card>

        <b-card v-if="rastreioMensagem" class="mt-1">
          <b-alert variant="warning" show>
            <div class="alert-body">
              {{ rastreioMensagem }}
            </div>
          </b-alert>
        </b-card>
      </b-col>
    </b-row>

    <invoice-sidebar-send-invoice />
    <invoice-sidebar-add-payment />
  </section>
</template>

<script>
import { ref, computed } from '@vue/composition-api'
import store from '@/store'
import router from '@/router'
import {
  BRow, BCol, BCard, BCardBody, BTableLite, BCardText, BButton, BAlert, BLink, BSpinner, BBadge, VBToggle,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { getUserData } from '@/auth/utils' 
import {currency, currencyIntCents, formatDateBr, formatDateTimeBr, formatDocumento, formatPhone} from '@/@core/utils/filter'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue'
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue'
import InvoiceSidebarSendInvoice from '../InvoiceSidebarSendInvoice.vue'
import InvoiceSidebarAddPayment from '../InvoiceSidebarAddPayment.vue'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'


export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BTableLite,
    BCardText,
    BButton,
    BAlert,
    BLink,
    BSpinner,
    BBadge,
    AppTimeline,
    AppTimelineItem,
    InvoiceSidebarAddPayment,
    InvoiceSidebarSendInvoice,
  },
  filters: {
    currency,
    currencyIntCents,
    formatDateBr,
    formatDateTimeBr,
    formatPhone,
    formatDocumento,
  },
  setup() {
    const toast = useToast()
    const invoiceData = ref(null)
    const paymentDetails = ref({})

    const resolverMetodoPagamento = metodoPagamento => {
      switch (metodoPagamento) {
        case 1:
          return 'Cartão (À vista)'
        case 2:
          return 'A prazo'
        case 3:
          return 'Pix'
        case 4:
          return 'Cartão (Parcelado)'
        case 5:
          return 'Boleto'
        case 6:
          return 'Cartão'
        case 7:
          return 'Cheque'
        default:
          return 'Não informado'
      }
    }

    // Invoice Description
    // ? Your real data will contain this information
    const pedidoItems = ref([])

    const userData = getUserData()

    const filteredPedidoItems = computed(() => {
      if (!pedidoItems.value || !userData || !userData.vendedor) return []
      // Se for admin, mostra todos os itens
      if (userData.id_grupousuario === 1) {
        return pedidoItems.value
      }
      // Filtra apenas itens do vendedor logado
      return pedidoItems.value.filter(item =>
        item.item.id_vendedor === userData.vendedor.id,
      )
    })

    const valorTotalFiltrado = computed(() => {
      if (!filteredPedidoItems.value || filteredPedidoItems.value.length === 0) {
        return 0
      }
      return filteredPedidoItems.value.reduce((total, item) => {
        const valorItem = Number(item.valor_total_item) || 0
        return total + valorItem
      }, 0)
    })

    const downloadLink = (pedido, nameField) => {
      const a = document.createElement('a')
      a.target = '_blank'
      a.href = pedido[nameField]
      a.download = `pedido-${pedido.id}_NFe-${pedido.bling_numero_nfe}.pdf`
      a.click()
    }

    store.dispatch('appClientPedidos/findById', { id: router.currentRoute.params.id })
      .then(response => {
        invoiceData.value = response.data
        pedidoItems.value = response.data.pedido_itens
        paymentDetails.value = response.data.condicao_pagamento
      })
      .catch(error => {
        if (error.response.status === 404) {
          invoiceData.value = undefined
        }
      })

    const printInvoice = () => {
      // Cria uma nova janela para impressão
      const printWindow = window.open('', '_blank')
      
      // Gera o HTML customizado para impressão
      const printHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Pedido #${invoiceData.value.id}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              font-size: 12px;
              background: #f8f9fa;
            }
            .invoice-container {
              max-width: 400px;
              margin: 0 auto;
              background: white;
              border-radius: 8px;
              overflow: hidden;
              box-shadow: 0 4px 8px rgba(0,0,0,0.1);
            }
            .header {
              background: linear-gradient(135deg, #7367f0, #9c88ff);
              color: white;
              padding: 20px;
              text-align: center;
            }
            .header h2 {
              margin: 0 0 5px 0;
              font-size: 18px;
              font-weight: bold;
            }
            .header p {
              margin: 0;
              font-size: 11px;
              opacity: 0.9;
            }
            .content {
              padding: 20px;
            }
            .section-title {
              color: #7367f0;
              font-weight: bold;
              font-size: 13px;
              margin: 15px 0 8px 0;
              text-transform: uppercase;
            }
            .detail-row {
              display: flex;
              justify-content: space-between;
              margin: 3px 0;
              font-size: 11px;
            }
            .detail-label {
              color: #666;
            }
            .detail-value {
              font-weight: bold;
              color: #333;
            }
            .products-table {
              width: 100%;
              border-collapse: collapse;
              margin: 10px 0;
            }
            .products-table th {
              background: #f8f9fa;
              padding: 8px 4px;
              text-align: left;
              font-size: 10px;
              font-weight: bold;
              color: #666;
              border-bottom: 1px solid #dee2e6;
            }
            .products-table td {
              padding: 6px 4px;
              font-size: 10px;
              border-bottom: 1px solid #f1f1f1;
            }
            .total-section {
              background: #f8f9fa;
              padding: 15px;
              margin: 15px -20px -20px -20px;
              text-align: right;
            }
            .total-row {
              display: flex;
              justify-content: space-between;
              margin: 3px 0;
              font-size: 12px;
            }
            .total-final {
              font-weight: bold;
              font-size: 16px;
              color: #7367f0;
              border-top: 1px solid #dee2e6;
              padding-top: 8px;
              margin-top: 8px;
            }
            @media print {
              body { background: white; }
              .invoice-container { box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <div class="invoice-container">
            <div class="header">
              <h2>ATIVADORES DE VENDAS</h2>
              <p>avmarket.com.br | (11) 9999-9999</p>
            </div>
            
            <div class="content">
              <div class="section-title">Detalhes do Pedido</div>
              <div class="detail-row">
                <span class="detail-label">Nº Pedido:</span>
                <span class="detail-value">#${invoiceData.value.id}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Data:</span>
                <span class="detail-value">${new Date(invoiceData.value.data_inclusao).toLocaleDateString('pt-BR')}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Cliente:</span>
                <span class="detail-value">${invoiceData.value.cliente.nome}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">CPF:</span>
                <span class="detail-value">${invoiceData.value.cliente.documento}</span>
              </div>
              
              <div class="section-title">Produtos</div>
              <table class="products-table">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th>Qtd</th>
                    <th>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  ${filteredPedidoItems.value.map(item => `
                    <tr>
                      <td>${item.item.item_nome}</td>
                      <td>x${item.quantidade}</td>
                      <td>R$ ${Number(item.valor_total_item).toFixed(2).replace('.', ',')}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
              
              <div class="total-section">
                <div class="total-row total-final">
                  <span>TOTAL: R$ ${Number(valorTotalFiltrado.value).toFixed(2).replace('.', ',')}</span>
                </div>
              </div>

              ${invoiceData.value.metodo_envio || invoiceData.value.codigo_rastreio ? `
              <div style="margin-top: 15px; padding: 10px; background: #f8f9fa; border-radius: 4px; font-size: 11px;">
                <strong>Envio:</strong>
                ${invoiceData.value.metodo_envio ? `${invoiceData.value.metodo_envio}` : ''}
                ${invoiceData.value.prazo_entrega ? ` — ${invoiceData.value.prazo_entrega} dias úteis` : ''}
                ${invoiceData.value.codigo_rastreio ? `<br><strong>Rastreio:</strong> ${invoiceData.value.codigo_rastreio}` : ''}
              </div>
              ` : ''}
            </div>
          </div>
        </body>
        </html>
      `
      
      // Escreve o HTML na nova janela
      printWindow.document.write(printHTML)
      printWindow.document.close()
      
      // Aguarda o carregamento e executa a impressão
      printWindow.onload = () => {
        printWindow.print()
        printWindow.close()
      }
    }

    const gerandoEtiqueta = ref(false)
    const baixandoEtiqueta = ref(false)
    const rastreando = ref(false)
    const rastreioEventos = ref([])
    const rastreioMensagem = ref('')

    const resolverCorEvento = descricao => {
      if (!descricao) return 'primary'
      const desc = descricao.toLowerCase()
      if (desc.includes('entregue') || desc.includes('entrega efetuada')) return 'success'
      if (desc.includes('saiu para entrega')) return 'info'
      if (desc.includes('postado') || desc.includes('objeto postado')) return 'warning'
      if (desc.includes('devolvido') || desc.includes('não entregue') || desc.includes('tentativa')) return 'danger'
      return 'primary'
    }

    const rastrearEncomenda = async () => {
      if (!invoiceData.value || !invoiceData.value.codigo_rastreio) return
      rastreando.value = true
      rastreioMensagem.value = ''
      rastreioEventos.value = []
      try {
        const { data } = await store.dispatch('appClientPedidos/rastrearEncomenda', {
          codigoRastreio: invoiceData.value.codigo_rastreio,
        })
        if (data && data.eventos && data.eventos.length > 0) {
          rastreioEventos.value = data.eventos
        } else {
          rastreioMensagem.value = data?.mensagem || 'Nenhum evento de rastreamento encontrado.'
        }
      } catch (error) {
        console.error('Erro ao rastrear encomenda:', error)
        rastreioMensagem.value = error?.response?.data?.mensagem || 'Erro ao consultar rastreamento.'
      } finally {
        rastreando.value = false
      }
    }

    const gerarEtiquetaCorreios = async () => {
      if (!invoiceData.value) return
      gerandoEtiqueta.value = true
      try {
        const { data } = await store.dispatch('appClientPedidos/gerarEtiquetaCorreios', {
          pedidoId: invoiceData.value.id,
        })
        if (data && data.codigo_rastreio) {
          invoiceData.value.codigo_rastreio = data.codigo_rastreio
          invoiceData.value.etiqueta_url = data.codigo_rastreio
        } else if (data && data.id_prepostagem) {
          invoiceData.value.etiqueta_url = data.id_prepostagem
        }
        const isLocal = data?.tipo === 'local'
        toast({
          component: ToastificationContent,
          props: {
            title: isLocal ? 'Etiqueta local gerada' : 'Etiqueta Correios gerada!',
            icon: isLocal ? 'AlertCircleIcon' : 'CheckCircleIcon',
            variant: isLocal ? 'warning' : 'success',
            text: data?.message || (isLocal ? 'API dos Correios indisponível. Use "Imprimir Etiqueta" para a etiqueta local.' : 'Clique em "Imprimir Etiqueta" para baixar o PDF.'),
          },
        })
      } catch (error) {
        console.error('Erro ao gerar etiqueta:', error)
        toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao gerar etiqueta',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error?.response?.data?.error || error?.message || 'Não foi possível gerar a etiqueta.',
          },
        })
      } finally {
        gerandoEtiqueta.value = false
      }
    }

    const isLocalLabel = () => {
      return invoiceData.value?.etiqueta_url?.startsWith('LOCAL-')
    }

    const imprimirEtiqueta = async () => {
      if (!invoiceData.value || !invoiceData.value.etiqueta_url) return
      baixandoEtiqueta.value = true
      try {
        if (isLocalLabel()) {
          const response = await store.dispatch('appClientPedidos/getEtiquetaLocalHtml', {
            pedidoId: invoiceData.value.id,
          })
          const blob = new Blob([response.data], { type: 'text/html; charset=utf-8' })
          const blobUrl = URL.createObjectURL(blob)
          const newTab = window.open(blobUrl, '_blank')
          if (!newTab) {
            toast({
              component: ToastificationContent,
              props: {
                title: 'Popup bloqueado',
                icon: 'AlertTriangleIcon',
                variant: 'warning',
                text: 'Permita popups neste site para abrir a etiqueta de impressão.',
              },
            })
          }
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
          baixandoEtiqueta.value = false
          return
        } else {
          const response = await store.dispatch('appClientPedidos/downloadEtiquetaPdf', {
            idPrePostagem: invoiceData.value.etiqueta_url,
          })
          const blob = new Blob([response.data], { type: 'application/pdf' })
          const blobUrl = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = blobUrl
          a.download = `etiqueta-pedido-${invoiceData.value.id}.pdf`
          a.click()
          window.URL.revokeObjectURL(blobUrl)
        }
      } catch (error) {
        console.error('Erro ao baixar etiqueta:', error)
        toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao imprimir etiqueta',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error?.response?.data?.error || error?.message || 'Não foi possível obter a etiqueta.',
          },
        })
      } finally {
        baixandoEtiqueta.value = false
      }
    }

    return {
      invoiceData,
      paymentDetails,
      pedidoItems,
      filteredPedidoItems,
      valorTotalFiltrado,
      printInvoice,
      resolverMetodoPagamento,
      downloadLink,
      gerandoEtiqueta,
      gerarEtiquetaCorreios,
      baixandoEtiqueta,
      imprimirEtiqueta,
      rastreando,
      rastreioEventos,
      rastreioMensagem,
      rastrearEncomenda,
      resolverCorEvento,
    }
  },
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";
</style>

<style lang="scss">
@media print {

  // Global Styles
  body {
    background-color: transparent !important;
  }
  nav.header-navbar {
    display: none;
  }
  .main-menu {
    display: none;
  }
  .header-navbar-shadow {
    display: none !important;
  }
  .content.app-content {
    margin-left: 0;
    padding-top: 2rem !important;
  }
  footer.footer {
    display: none;
  }
  .card {
    background-color: transparent;
    box-shadow: none;
  }
  .customizer-toggle {
    display: none !important;
  }

  // Invoice Specific Styles
  .invoice-preview-wrapper {
    .row.invoice-preview {
      .col-md-8 {
        max-width: 100%;
        flex-grow: 1;
      }

      .invoice-preview-card {
        .card-body:nth-of-type(2) {
          .row {
              > .col-12 {
              max-width: 50% !important;
            }

            .col-12:nth-child(2) {
              display: flex;
              align-items: flex-start;
              justify-content: flex-end;
              margin-top: 0 !important;
            }
          }
        }
      }
    }

    // Action Right Col
    .invoice-actions {
      display: none;
    }
  }
}
</style>
